import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPageMomentAdvantage.module.less'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import cls from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SliderModels } from '@/types/slider'
import PptPage, { PageHeader, } from '../PptPage/PptPage'
import { useSize } from 'ahooks'

interface Props {
  project: ProjectModels.ProjectDetail
  no: number
  advantage: string
  disadvantage: string
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}

function PptPageMomentAdvantage({
  project, onSliderReady, no, advantage, disadvantage, }: Props) {
  const { t } = useTranslation()
  const BOX_MAX_HEIGHT = 650
  const [ratio, setRatio] = useState<number>(1)
  const [ratioFinished, setRatioFinished] = useState<boolean>(false)
  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()

  const title1Ref = useRef<HTMLDivElement>(null)
  const title1IconRef = useRef<HTMLImageElement>(null)
  const title1TextRef = useRef<HTMLSpanElement>(null)
  const title2Ref = useRef<HTMLDivElement>(null)
  const title2IconRef = useRef<HTMLImageElement>(null)
  const title2TextRef = useRef<HTMLSpanElement>(null)

  const [advantageChildren, setAdvantageChildren] = useState<HTMLCollection>()
  const [disadvantageChildren, setDisadvantageChildren] = useState<HTMLCollection>()


  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }
  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  const advantageRef = useRef<HTMLDivElement>(null)
  const disadvantageRef = useRef<HTMLDivElement>(null)

  const advantageSize = useSize(advantageRef.current)
  const disadvantageSize = useSize(disadvantageRef.current)

  useEffect(() => {
    if (
      !title1Ref.current ||
      !title1IconRef.current ||
      !title1TextRef.current ||
      !title2Ref.current ||
      !title2IconRef.current ||
      !title2TextRef.current ||
      !advantageRef.current ||
      !disadvantageRef.current ||
      !advantageChildren ||
      !disadvantageChildren
    ) {
      return
    }
    const title1Rect = title1Ref.current.getBoundingClientRect()
    const title2Rect = title2Ref.current.getBoundingClientRect()
    const advantageRect = advantageRef.current.getBoundingClientRect()
    const disadvantageRect = disadvantageRef.current.getBoundingClientRect()

    function generateShapeInfo(
      boxRect: DOMRect,
      childrenLength: number
    ): Omit<SliderModels.PPTShapeElement, 'fill' | 'gradient'> {
      const SHAPE_RATIO = 20 / BOX_MAX_HEIGHT
      const SHAPE_WIDTH = boxRect.width
      const SHAPE_HEIGHT = boxRect.height
      const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
      const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
      const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO
      return {
        type: 'shape',
        x: boxRect.x,
        y: boxRect.y,
        width: boxRect.width,
        height: boxRect.height + childrenLength * 30,
        viewBox: [boxRect.width, boxRect.height],
        path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
        fixedRatio: false,
        rotate: 0,
        pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
        keypoints: [SHAPE_RATIO],
      }
    }

    function genertateBoxElement(
      boxRect: DOMRect,
      iconDom: HTMLImageElement,
      textDom: HTMLSpanElement,
      color: string
    ): SliderModels.PPTElement[] {
      const iconRect = iconDom.getBoundingClientRect()
      const textRect = textDom.getBoundingClientRect()
      const SHAPE_RATIO = 6 / boxRect.height
      // 因为各个方块的大小高度其实是一致的，所以这里统一设置
      const SHAPE_WIDTH = boxRect.width
      const SHAPE_HEIGHT = boxRect.height
      const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
      const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
      const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO

      return [
        {
          type: 'shape',
          x: boxRect.x,
          y: boxRect.y,
          width: boxRect.width,
          height: boxRect.height,
          viewBox: [boxRect.width, boxRect.height],
          path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
          fill: color,
          fixedRatio: false,
          rotate: 0,
          pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
          keypoints: [SHAPE_RATIO],
        },
        {
          type: 'image',
          x: iconRect.x,
          y: iconRect.y,
          width: iconRect.width,
          height: iconRect.height,
          src: iconDom.src,
        },
        {
          type: 'text',
          content: textDom.outerHTML,
          x: textRect.x,
          y: textRect.y - 60,
          width: textRect.width,
          height: textRect.height,
        },
      ]
    }

    const elements: SliderModels.PPTElement[] = [
      ...genertateBoxElement(title1Rect, title1IconRef.current, title1TextRef.current, '#6D54FA'),
      {
        ...generateShapeInfo(advantageRect, advantageChildren.length),
        fill: '#f0f0ff',
        // NOTE: 这里注释掉是因为 pptxgenjs 不支持 gradient 的设置
        // gradient: {
        //   type: 'linear',
        //   rotate: 22,
        // colors: [
        //   { pos: 0, color: '#F4F4FE' },
        //   { pos: 50, color: '#D9D8FF' },
        //   { pos: 100, color: '#F6F6FF' },
        //   ],
        // },
        outline: {
          width: 1,
          color: '#D5D4FC',
          style: 'solid',
        },
      },
      ...Array.from(advantageChildren).map((el) => {
        const rect = el.getBoundingClientRect()
        return {
          type: 'text',
          content: el.outerHTML.replace(/\n/g, ''),
          x: rect.x,
          y: rect.y - 30,
          width: rect.width,
          height: rect.height,
        } as SliderModels.PPTTextElement
      }),
      ...genertateBoxElement(title2Rect, title2IconRef.current, title2TextRef.current, '#515366'),
      {
        ...generateShapeInfo(disadvantageRect, disadvantageChildren.length),
        fill: '#f8f8ff',
        // NOTE: 这里注释掉是因为 pptxgenjs 不支持 gradient 的设置
        // gradient: {
        //   type: 'linear',
        //   rotate: 22,
        //   colors: [
        //     { pos: 0, color: '#F4F4FE' },
        //     { pos: 50, color: '#D9D8FF' },
        //     { pos: 100, color: '#F6F6FF' },
        //   ],
        // },
        outline: {
          width: 1,
          color: '#D5D4FC',
          style: 'solid',
        },
      },
      ...Array.from(disadvantageChildren).map((el) => {
        const rect = el.getBoundingClientRect()
        return {
          type: 'text',
          content: el.outerHTML.replace(/\n/g, ''),
          x: rect.x,
          y: rect.y - 30,
          width: rect.width,
          height: rect.height,
        } as SliderModels.PPTTextElement
      }),
    ]
    setElements(elements)
  }, [advantageChildren, disadvantageChildren,])

  useEffect(() => {
    if (!ratioFinished || !project || !advantageRef.current || !disadvantageRef.current) {
      return
    }
    const advantageChildren = advantageRef.current.children
    const disadvantageChildren = disadvantageRef.current.children
    for (const child of advantageChildren) {
      const c = child as HTMLElement
      c.style.fontSize = `${24 * ratio}px`
      c.style.color = '#2A2C3F'
    }
    for (const child of disadvantageChildren) {
      const c = child as HTMLElement
      c.style.fontSize = `${24 * ratio}px`
      c.style.color = '#2A2C3F'
    }
    setAdvantageChildren(advantageChildren)
    setDisadvantageChildren(disadvantageChildren)
  }, [project, ratio, ratioFinished])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  // useEffect(() => {
  //   if (advantageRef.current && disadvantageRef.current) {
  //     const adHeight = advantageRef.current.getBoundingClientRect().height
  //     const disHeight = disadvantageRef.current.getBoundingClientRect().height
  //     const bothMaxHeight = Math.max(adHeight, disHeight)
  //     if (bothMaxHeight > BOX_MAX_HEIGHT) {
  //       const ratio = BOX_MAX_HEIGHT / bothMaxHeight
  //       setRatio(ratio)
  //     }
  //     setRatioFinished(true)
  //   }
  // }, [])

  useEffect(() => {
    if (advantageSize && disadvantageSize) {
      const adHeight = advantageSize.height
      const disHeight = disadvantageSize.height
      const bothMaxHeight = Math.max(adHeight, disHeight)
      if (bothMaxHeight > BOX_MAX_HEIGHT) {
        setRatio((ratio) => ratio - 0.1)
      } else {
        setRatioFinished(true)
      }
    }
  }, [advantageSize, disadvantageSize])

  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PPTDownload/bg-7.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPageMomentAdvantage}>
        <PageHeader
          project={project}
          text={'03.' + t('dashboard.projectDetailPage.detailAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={styles.content} style={{ paddingTop: 100 * ratio }}>
          <div className={styles.item}>
            <div className={styles.itemInner}>
              <div className={styles.title} ref={title1Ref}>
                <img
                  ref={title1IconRef}
                  style={{ width: 36, height: 36 }}
                  src={commonUtils.getImagePath('PDFDownload/advantage-icon.png')}
                  alt=''
                />
                <span
                  ref={title1TextRef}
                  style={{ fontSize: 36, color: '#fff', fontWeight: 'bold' }}
                >
                  {t('dashboard.projectDetailPage.storyboardView.advantages')}
                </span>
              </div>
              <div className={styles.advantage} ref={advantageRef}>
                <MarkdownRenderer
                  style={{ fontSize: 24 * ratio }}
                  markdownText={advantage}
                />
              </div>
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInner}>
              <div className={cls(styles.title, styles.disadvantageTitle)} ref={title2Ref}>
                <img
                  ref={title2IconRef}
                  style={{ width: 36, height: 36 }}
                  src={commonUtils.getImagePath('PDFDownload/disadvantage-icon.png')}
                  alt=''
                />
                <span
                  ref={title2TextRef}
                  style={{ fontSize: 36, color: '#fff', fontWeight: 'bold' }}
                >
                  {t('dashboard.projectDetailPage.storyboardView.disadvantages')}
                </span>
              </div>
              <div className={cls(styles.advantage, styles.disadvantage)} ref={disadvantageRef}>
                <MarkdownRenderer
                  style={{ fontSize: 24 * ratio }}
                  markdownText={disadvantage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PptPage>
  )
}

export default PptPageMomentAdvantage
